import React, {
    useState,
    useCallback,
    useEffect,
    useRef,
} from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import Footer from './Footer';
import { tid } from '@/utils/testId';

const FooterVisibility: React.FC = () => {
    const mainRef = useRef<HTMLElement | null>(null);
    const [showFooter, setShowFooter] = useState(false);
    const location = useLocation();

    const scrollTargetRef = useRef<HTMLElement | null>(null);

    const ticking = useRef(false);

    const updateShowFooter = useCallback(() => {
        if (ticking.current) return;

        ticking.current = true;

        window.requestAnimationFrame(() => {
            const el =
                scrollTargetRef.current ?? mainRef.current;

            if (!el) {
                ticking.current = false;
                return;
            }

            const remaining =
                el.scrollHeight -
                el.scrollTop -
                el.clientHeight;

            setShowFooter((prev) => {
                // Footer hidden → show only when very close to bottom
                if (!prev) {
                    return remaining <= 20;
                }

                // Footer visible → keep it visible until user scrolls up more
                return remaining <= 60;
            });

            ticking.current = false;
        });
    }, []);

    const bindScrollTarget = useCallback(() => {
        const root = mainRef.current;

        if (!root) return;

        const inner =
            root.querySelector<HTMLElement>(
                '[data-footer-scrollable]',
            );

        const next = inner ?? root;

        if (next === scrollTargetRef.current) {
            updateShowFooter();
            return;
        }

        if (scrollTargetRef.current) {
            scrollTargetRef.current.removeEventListener(
                'scroll',
                updateShowFooter,
            );
        }

        scrollTargetRef.current = next;

        scrollTargetRef.current.addEventListener(
            'scroll',
            updateShowFooter,
            { passive: true },
        );

        updateShowFooter();
    }, [updateShowFooter]);

    useEffect(() => {
        bindScrollTarget();
    }, [location.pathname, bindScrollTarget]);

    useEffect(() => {
        const root = mainRef.current;

        if (!root) return undefined;

        let ro: ResizeObserver | null = null;

        if (window.ResizeObserver) {
            ro = new ResizeObserver(() => {
                bindScrollTarget();
            });

            ro.observe(root);
        }

        const mo = new MutationObserver(() => {
            bindScrollTarget();
        });

        mo.observe(root, {
            childList: true,
            subtree: true,
        });

        window.addEventListener(
            'resize',
            updateShowFooter,
        );

        bindScrollTarget();

        return () => {
            mo.disconnect();

            if (ro) {
                ro.disconnect();
            }

            window.removeEventListener(
                'resize',
                updateShowFooter,
            );

            if (scrollTargetRef.current) {
                scrollTargetRef.current.removeEventListener(
                    'scroll',
                    updateShowFooter,
                );
            }
        };
    }, [bindScrollTarget, updateShowFooter]);

    return (
        <>
            <main
                ref={mainRef}
                data-testid={tid('page-outlet')}
                className="flex-1 flex flex-col min-h-0 overflow-y-auto px-4 sm:px-5 pt-3 sm:pt-4"
            >
                <Outlet />
            </main>

            <div
                style={{
                    height: showFooter ? 'auto' : 0,
                    overflow: 'hidden',
                    flexShrink: 0,
                }}
            >
                <Footer />
            </div>

        </>
    );
};

export default FooterVisibility;